<script>
import treatmentService from "@/services/treatment";
import episodeService from "@/services/episode";
import format from "date-fns/format";
import isToday from "date-fns/isToday";
import Spinner from "@/components/ui/Spinner";
import segmentService from "@/services/segment";
import { capitalize } from "@/services/helper";

/**
 * @component PatientTreatmentDetail
 * @description Pantalla de asignación de protocolos a un paciente. Layout v3:
 *              - Page-header con back + título + acciones (Cancelar / Asignar)
 *              - Sección "Asignar nuevo protocolo": form con select Protocolo
 *                + date-picker Fecha de inicio
 *              - Sección "Protocolos asignados": toggle "solo asignados" en
 *                el header de la sección + tabla
 *              Layout legacy (modal fixed con header azul) disponible como
 *              fallback via `localStorage.patientTreatmentDetailLegacyLayout = '1'`.
 */
export default {
  name: "PatientTreatmentDetail",

  components: {
    Spinner,
  },

  data() {
    return {
      isFetching: false,
      isPosting: false,
      patientId: this.$route.params.id,
      showAssignedOnly: true,
      treatments: [],
      episodes: [],
      treatmentAssignments: [],
      treatmentAssignmentsDisabled: [],
      newTreatment: {
        id: "",
        startsAt: "",
      },
      filterRange: {
        from: "",
        to: "",
      },
    };
  },

  created() {
    this.isFetching = true;
    Promise.all([this.getTreatments(), this.getTreatmentAssignmets()]).finally(
      () => {
        this.isFetching = false;
      }
    );

    document.addEventListener("keyup", this.onKeyUp);
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.onKeyUp);
  },

  watch: {
    showAssignedOnly() {
      this.getTreatmentAssignmets();
    },
  },

  computed: {
    /**
     * @description Fallback al layout legacy (modal fixed con banner azul).
     *              Activable via:
     *                localStorage.setItem('patientTreatmentDetailLegacyLayout', '1')
     *              y reload. `removeItem` para volver al nuevo.
     * @returns {boolean}
     */
    useLegacyLayout() {
      try {
        return (
          typeof localStorage !== "undefined" &&
          localStorage.getItem("patientTreatmentDetailLegacyLayout") === "1"
        );
      } catch (e) {
        return false;
      }
    },

    filteredTreatmentAssignments() {
      if (!this.filterRange.from && !this.filterRange.to) {
        if (this.showAssignedOnly) {
          return this.treatmentAssignments.filter(
            ({ unassignedAt }) => !unassignedAt
          );
        } else {
          return this.treatmentAssignments;
        }
      }

      const from =
        this.filterRange.from && format(this.filterRange.from, "dd/MM/yyyy");
      const to =
        this.filterRange.to && format(this.filterRange.to, "dd/MM/yyyy");

      return this.treatmentAssignments.filter((treatment) => {
        const createdAt = format(treatment.createdAt, "dd/MM/yyyy");

        if (this.filterRange.from && this.filterRange.to) {
          return createdAt >= from && createdAt <= to;
        } else if (this.filterRange.from) {
          return createdAt >= from;
        } else {
          return createdAt <= to;
        }
      });
    },

    parsedTreatments() {
      return this.treatments.map((treatment) => {
        treatment.disabled = this.treatmentAssignments.some(
          (treatmentAssignment) => {
            return (
              treatmentAssignment.treatment &&
              treatmentAssignment.treatment._id === treatment._id &&
              !treatmentAssignment.unassignedAt
            );
          }
        );
        return treatment;
      });
    },
  },

  methods: {
    closeModal() {
      this.$router.back();
    },

    onKeyUp(event) {
      const esc = 27;
      if (event.keyCode == esc) {
        this.closeModal();
      }
    },

    getTreatments() {
      return treatmentService.get().then((res) => {
        this.treatments = res.docs;
      });
    },

    getTreatmentAssignmets() {
      episodeService.get(this.patientId).then((res) => {
        this.episodes = res.docs;
        this.treatmentAssignments = this.episodes.flatMap(
          (episode) => episode.treatments
        );
      });
    },

    submit() {
      const alreadyHasAssignedProtocols = this.treatmentAssignments.filter(
        ({ unassignedAt }) => !unassignedAt
      );

      if (alreadyHasAssignedProtocols && alreadyHasAssignedProtocols.length) {
        const protocolsNames = alreadyHasAssignedProtocols
          .map(({ treatment }) => capitalize(treatment.name))
          .join(", ");

        const message = `El paciente ya tiene protocolo/s asignados: <br/>
            <b>${protocolsNames}</b>
            <br/>
            Desea continuar?`;

        this.$confirm(message, "Confirmar", {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          dangerouslyUseHTMLString: true,
          type: "warning",
        })
          .then(() => {
            this.assignTreatmentToPatient();
          })
          .catch(() => {});
      } else {
        this.assignTreatmentToPatient();
      }
    },

    assignTreatmentToPatient() {
      this.isPosting = true;
      return episodeService
        .createEpisodeTreatment({
          patientId: this.patientId,
          treatmentId: this.newTreatment.id,
          // El datepicker no toma hora; si la fecha es hoy puede causar problemas
          // de validación según la hora actual. Usamos `new Date()` en ese caso.
          startsAt: isToday(this.newTreatment.startsAt)
            ? new Date()
            : this.newTreatment.startsAt,
        })
        .then(() => {
          segmentService.track({ name: "TreatmentAssigned Created" });
          this.newTreatment = { id: "", startsAt: "" };
          return this.getTreatmentAssignmets();
        })
        .finally(() => {
          this.isPosting = false;
        });
    },

    isUnassignDisabled(treatmentId) {
      return this.treatmentAssignmentsDisabled.some((id) => id === treatmentId);
    },

    unassignTreatment(treatmentAssignment) {
      const { _id, treatment } = treatmentAssignment;
      const { name } = { ...treatment };
      const msg = name ? `: "${name}"` : "";
      this.$confirm(
        `¿Desea desasignar el protocolo${msg}?`,
        "Desasignar Protocolo",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      ).then(() => {
        segmentService.track({ name: "TreatmentAssignment Removed" });
        this.treatmentAssignmentsDisabled.push(_id);

        episodeService
          .removeEpisodeTreatment({
            patientId: this.patientId,
            episodeTreatmentId: _id,
          })
          .then(this.getTreatmentAssignmets)
          .finally(() => {
            this.treatmentAssignmentsDisabled =
              this.treatmentAssignmentsDisabled.filter((id) => id !== _id);
          });
      });
    },
  },
};
</script>

<template lang="pug">
  ValidationObserver(v-slot="{handleSubmit}")
    //- ─────────────────────────────────────────────────────────────────
    //- LEGACY LAYOUT — modal fixed con header azul + .modal__sign vertical.
    //- Activable via `localStorage.patientTreatmentDetailLegacyLayout='1'`.
    //- Se mantiene byte-por-byte como estaba para que sea un escape válido.
    //- ─────────────────────────────────────────────────────────────────
    form.modal.treatment-detail(
      v-if="useLegacyLayout"
      @submit.prevent="handleSubmit(submit)"
    )
      header.modal__header
        h2.modal__title Protocolo
        .modal__actions
          el-button(type="info", @click="closeModal()") Salir
          el-button.border(
            type="primary",
            native-type="submit",
            :loading="isPosting"
          ) Asignar

      .modal__content(v-if="isFetching")
        spinner
      .modal__content(v-else)
        .modal__block
          .modal__section
            .modal__sign.sign
              .sign__icon
                micon(name="note_add")
              h3.sign__title Asignar Nuevo Protocolo
            article.modal__fields
              .modal__row
                fieldset.modal__field
                  label.label Protocolo:
                  ValidationProvider(name="Protocolo", rules="required", v-slot="{ errors }")
                    el-select(
                      v-model="newTreatment.id",
                      filterable,
                      default-first-option
                    )
                      el-option(
                        v-for="treatment in parsedTreatments",
                        :key="treatment._id",
                        :label="treatment.name",
                        :value="treatment._id",
                        :disabled="treatment.disabled"
                      )
                    span.has-error {{ errors[0] }}
              .modal__row
                fieldset.modal__field
                  label.label Seleccione una fecha
                  ValidationProvider(name="Fecha", rules="required|includeToday", v-slot="{ errors }")
                    el-date-picker(
                      v-model="newTreatment.startsAt"
                      :disabled-time="false"
                      lang="es",
                      :class="{ 'has-error': errors[0] }",
                      :not-before="new Date()"
                    )
                    span.has-error {{ errors[0] }}
            article.modal__fields
        .modal__block
          .modal__section
            .modal__sign.sign
              .sign__icon
                micon(name="description")
              h3.sign__title Protocolos Asignados
            article.modal__fields
              .modal__row
                fieldset.modal__field
                  el-checkbox(
                    v-if="!!treatmentAssignments.length"
                    v-model="showAssignedOnly"
                    :label="'Mostrar solo Protocolos asignados'"
                  )

              .modal__row
                fieldset.modal__field
                  h2.no-treatments(
                    v-if="!filteredTreatmentAssignments.length && (filterRange.from || filterRange.to)"
                  )
                    | No hay protocolos asignados en el rango de fechas
                  h2.no-treatments(v-else-if="!filteredTreatmentAssignments.length")
                    | No tiene protocolos asignados
                  .box(v-else)
                    table
                      thead
                        tr
                          th Nombre
                          th Fecha de asignación
                          th Fecha de desasignación
                          th Primera Tarea
                          th Última Tarea
                          th Acciones
                      tbody
                        tr(
                          v-for="(treatmentAssignment, index) in filteredTreatmentAssignments",
                          :key="treatmentAssignment._id"
                        )
                          td {{ treatmentAssignment.treatment ? treatmentAssignment.treatment.name : 'ERROR' | capitalize }}
                          td {{ treatmentAssignment.createdAt | formatDateAndTime }}
                          td {{ treatmentAssignment.unassignedAt | formatDateAndTime }}
                          td {{ treatmentAssignment.firstTaskScheduledAt | formatDateAndTime }}
                          td {{ treatmentAssignment.lastTaskScheduledAt | formatDateAndTime }}
                          td.actions
                            .actions-container
                            button.button.button--action(
                              v-if="!treatmentAssignment.unassignedAt"
                              @click.prevent="unassignTreatment(treatmentAssignment)",
                              :disabled="isUnassignDisabled(treatmentAssignment._id)"
                              v-tooltip="'Desasignar protocolo'"
                            )
                              micon(name="delete_forever")

    //- ─────────────────────────────────────────────────────────────────
    //- NEW LAYOUT — page-header + secciones + section-title.
    //- ─────────────────────────────────────────────────────────────────
    form.patient-treatment-detail(v-else @submit.prevent="handleSubmit(submit)")
      .page-header
        .page-header__left
          el-button(type="text" icon="el-icon-arrow-left" @click="closeModal()") Paciente
          h1.page-title Protocolos
        .page-header__actions
          el-button(@click="closeModal()") Cancelar
          el-button(
            type="primary"
            :loading="isPosting"
            native-type="submit"
          ) Asignar

      p.page-hint Asigná un protocolo nuevo o gestioná los existentes

      .ptd__loading(v-if="isFetching")
        spinner

      template(v-else)
        //- ── Sección: Asignar nuevo protocolo ──────────────────────────
        section.ptd__section
          h3.section-title Asignar nuevo protocolo
          .ptd__form
            .ptd__field
              label.ptd__label
                | Protocolo
                span.ptd__req *
              ValidationProvider(name="Protocolo" rules="required" v-slot="{ errors }")
                el-select(
                  v-model="newTreatment.id"
                  filterable
                  default-first-option
                  placeholder="Buscar protocolo…"
                  style="width: 100%"
                )
                  el-option(
                    v-for="treatment in parsedTreatments"
                    :key="treatment._id"
                    :label="treatment.name"
                    :value="treatment._id"
                    :disabled="treatment.disabled"
                  )
                span.ptd__error(v-if="errors[0]") {{ errors[0] }}
            .ptd__field
              label.ptd__label
                | Fecha de inicio
                span.ptd__req *
              ValidationProvider(name="Fecha" rules="required|includeToday" v-slot="{ errors }")
                el-date-picker(
                  v-model="newTreatment.startsAt"
                  :disabled-time="false"
                  lang="es"
                  :class="{ 'has-error': errors[0] }"
                  :not-before="new Date()"
                  style="width: 100%"
                )
                span.ptd__error(v-if="errors[0]") {{ errors[0] }}

        //- ── Sección: Protocolos asignados ────────────────────────────
        section.ptd__section
          .ptd__section-head
            h3.section-title Protocolos asignados
            el-checkbox(
              v-if="!!treatmentAssignments.length"
              v-model="showAssignedOnly"
            ) Solo asignados

          //- Empty state
          .ptd__empty(v-if="!filteredTreatmentAssignments.length")
            micon.ptd__empty-icon(name="description")
            p.ptd__empty-title(
              v-if="filterRange.from || filterRange.to"
            ) No hay protocolos asignados en el rango de fechas
            p.ptd__empty-title(v-else) Este paciente no tiene protocolos asignados
            p.ptd__empty-hint(v-if="!(filterRange.from || filterRange.to)") Asigná uno arriba para arrancar.

          //- Tabla
          .ptd__table-wrap(v-else)
            table.ptd__table
              thead
                tr
                  th Nombre
                  th Asignación
                  th Desasignación
                  th 1ª tarea
                  th Última tarea
                  th.ptd__th-actions
              tbody
                tr(
                  v-for="treatmentAssignment in filteredTreatmentAssignments"
                  :key="treatmentAssignment._id"
                )
                  td.ptd__td-name {{ treatmentAssignment.treatment ? treatmentAssignment.treatment.name : 'ERROR' | capitalize }}
                  td {{ treatmentAssignment.createdAt | formatDateAndTime }}
                  td {{ treatmentAssignment.unassignedAt | formatDateAndTime }}
                  td {{ treatmentAssignment.firstTaskScheduledAt | formatDateAndTime }}
                  td {{ treatmentAssignment.lastTaskScheduledAt | formatDateAndTime }}
                  td.ptd__td-actions
                    el-tooltip(
                      content="Desasignar protocolo"
                      placement="left"
                      v-if="!treatmentAssignment.unassignedAt"
                    )
                      el-button(
                        type="text"
                        size="small"
                        :disabled="isUnassignDisabled(treatmentAssignment._id)"
                        @click.prevent="unassignTreatment(treatmentAssignment)"
                      )
                        micon.ptd__td-icon(name="delete_forever")
</template>

<style lang="scss" scoped>
.patient-treatment-detail {
  padding: 24px 32px 48px;
  background: #f6f8fb;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 4px;
}

.page-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-header__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #1a2540;
  letter-spacing: -0.01em;
}

.page-hint {
  font-size: 12px;
  color: #97a0b5;
  margin: 0 0 16px;
}

.ptd__loading {
  background: #fff;
  border-radius: 8px;
  padding: 60px 0;
  display: flex;
  justify-content: center;
}

.ptd__section {
  background: #fff;
  border-radius: 8px;
  padding: 18px 20px 22px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(20, 30, 60, 0.04),
    0 4px 16px rgba(20, 30, 60, 0.04);
}

.ptd__section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  .section-title {
    margin-bottom: 0;
  }
}

/* Form */
.ptd__form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;
}

.ptd__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.ptd__label {
  font-size: 12px;
  font-weight: 600;
  color: #1a2540;
}

.ptd__req {
  color: #d44a3a;
  margin-left: 2px;
}

.ptd__error {
  color: #d44a3a;
  font-size: 12px;
  margin-top: 4px;
}

/* Empty state */
.ptd__empty {
  text-align: center;
  padding: 32px 16px;
  border: 1px dashed #e4e7ee;
  border-radius: 8px;
}

.ptd__empty-icon {
  width: 32px;
  height: 32px;
  fill: #c5cdd9;
  margin-bottom: 8px;
}

.ptd__empty-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #5b6781;
}

.ptd__empty-hint {
  margin: 0;
  font-size: 12px;
  color: #97a0b5;
}

/* Table */
.ptd__table-wrap {
  overflow-x: auto;
  border: 1px solid #e4e7ee;
  border-radius: 8px;
}

.ptd__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 10px 14px;
    font-weight: 600;
    font-size: 12px;
    color: #6b7592;
    background: #fafbfd;
    border-bottom: 1px solid #e4e7ee;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f0f2f7;
    color: #1a2540;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: #fafbfd;
  }
}

.ptd__td-name {
  font-weight: 600;
}

.ptd__th-actions {
  width: 60px;
  text-align: right !important;
}

.ptd__td-actions {
  text-align: right;
}

.ptd__td-icon {
  width: 18px;
  height: 18px;
  fill: #97a0b5;

  &:hover {
    fill: #d44a3a;
  }
}
</style>
